import React, { useState, useEffect, useCallback } from "react";
import {
  Coins,
  CreditCard,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  CheckCircle2,
  X,
  RefreshCw,
  Clock,
  ArrowUpRight,
  ArrowDownLeft,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import * as api from "@/src/api/sdk.gen";
import type { CreditTransactionRow } from "@/src/api/types.gen";
import { useOrgDashboard } from "../context/OrgDashboardContext";

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  priceUsd: number;
  priceSar: number;
  badge?: string;
  description: string;
  features: string[];
}

const CREDIT_PACKAGES: CreditPackage[] = [
  {
    id: "starter",
    name: "Starter Pack",
    credits: 25,
    priceUsd: 49,
    priceSar: 185,
    description: "Ideal for piloting custom AI courses for small teams.",
    features: [
      "~25 Interactive Micro-Lessons",
      "Stage 2 RAG SOP Grounding",
      "Interactive MCQs & Flip Cards",
      "Instant Activation upon payment",
    ],
  },
  {
    id: "growth",
    name: "Growth Pack",
    credits: 100,
    priceUsd: 179,
    priceSar: 670,
    badge: "Most Popular",
    description: "Best for scaling corporate academies and departmental onboarding.",
    features: [
      "~100 Interactive Micro-Lessons",
      "High-Priority Worker Generation",
      "Multi-Workspace Syllabus Support",
      "Volume Savings (10% Discount)",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise Pack",
    credits: 500,
    priceUsd: 799,
    priceSar: 2999,
    badge: "Maximum Value",
    description: "Complete organizational curriculum transformation with full audit logging.",
    features: [
      "~500 Interactive Micro-Lessons",
      "Dedicated Generation Worker Throughput",
      "Unlimited SOP Knowledge Ingestion",
      "Enterprise Volume Savings (20%)",
    ],
  },
];

export const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    slug,
    stats,
    creditsSummary,
    loadingCredits,
    loadCredits,
    seatUtilization,
    loadStats,
  } = useOrgDashboard();

  // Buy Credits Modal state
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState<CreditPackage>(CREDIT_PACKAGES[1]);

  // Transactions ledger state
  const [transactions, setTransactions] = useState<CreditTransactionRow[]>([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [totalTx, setTotalTx] = useState(0);
  const [limit] = useState(15);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");

  const fetchTransactions = useCallback(async () => {
    if (!slug) return;
    try {
      setLoadingTx(true);
      const { data } =
        await api.getOrgCreditsTransactionsEndpointApiV1B2bSlugCreditsTransactionsGet({
          path: { slug },
          query: {
            limit,
            offset,
            status: statusFilter || undefined,
            type: typeFilter || undefined,
          },
        });
      if (data) {
        setTransactions(data.transactions || []);
        setTotalTx(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to load transactions ledger:", err);
    } finally {
      setLoadingTx(false);
    }
  }, [slug, limit, offset, statusFilter, typeFilter]);

  useEffect(() => {
    void fetchTransactions();
  }, [fetchTransactions]);

  const handlePurchase = () => {
    if (!slug || !selectedPkg) return;
    setIsBuyModalOpen(false);
    navigate(`/checkout?packageId=${selectedPkg.id}&org=${slug}`);
  };

  const handleSelectAndCheckout = (pkg: CreditPackage) => {
    if (!slug) return;
    navigate(`/checkout?packageId=${pkg.id}&org=${slug}`);
  };

  const getTxTypeBadge = (type: string) => {
    switch (type.toLowerCase()) {
      case "purchase":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <ArrowDownLeft className="w-3 h-3" /> Purchase
          </span>
        );
      case "settle":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <ArrowUpRight className="w-3 h-3" /> Course Settled
          </span>
        );
      case "hold":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3 h-3" /> Generation Hold
          </span>
        );
      case "refund":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
            <RefreshCw className="w-3 h-3" /> Refunded
          </span>
        );
      case "admin_grant":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
            <Sparkles className="w-3 h-3" /> Admin Grant
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            {type}
          </span>
        );
    }
  };

  const getStatusPill = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "settled":
        return (
          <span className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase bg-emerald-100 text-emerald-800">
            {status}
          </span>
        );
      case "held":
      case "pending":
        return (
          <span className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase bg-amber-100 text-amber-800">
            {status}
          </span>
        );
      case "refunded":
        return (
          <span className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase bg-purple-100 text-purple-800">
            {status}
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase bg-slate-100 text-slate-700">
            {status}
          </span>
        );
    }
  };

  const availableCredits = creditsSummary?.ai_credits_balance ?? 0;
  const totalPurchased = creditsSummary?.ai_credits_total_purchased ?? 0;
  const totalConsumed = creditsSummary?.ai_credits_total_consumed ?? 0;
  const creditsPerLesson = creditsSummary?.credits_per_lesson ?? 1.0;
  const estimatedLessons = Math.floor(availableCredits / creditsPerLesson);

  return (
    <div className="space-y-8">
      {/* Top Header & Overview */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-3xl shadow-xs">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
            <CreditCard className="w-6 h-6 text-indigo-600" />
            Billing, Quotas & AI Credits
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Real-time metering and usage ledger for enterprise course generation and seat quotas.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              void loadCredits(true);
              void fetchTransactions();
              void loadStats(true);
            }}
            disabled={loadingCredits}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition cursor-pointer"
            title="Refresh balance and transactions"
            aria-label="Refresh balance and transactions"
          >
            <RefreshCw className={`w-4 h-4 ${loadingCredits ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => setIsBuyModalOpen(true)}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-xs transition cursor-pointer"
          >
            <Zap className="w-4 h-4 text-amber-300 fill-amber-300" />
            Buy AI Credits
          </button>
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Available Balance Card */}
        <div className="bg-gradient-to-br from-indigo-900 to-slate-900 text-white p-6 rounded-3xl shadow-md relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 p-6 opacity-10 pointer-events-none">
            <Coins className="w-24 h-24 text-white" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-indigo-200">
                Available AI Credits
              </span>
              <span className="px-2 py-0.5 bg-indigo-500/30 border border-indigo-400/40 rounded-md text-[10px] font-bold uppercase text-indigo-100">
                Active Balance
              </span>
            </div>
            <div className="text-3xl font-extrabold tracking-tight">
              {availableCredits.toLocaleString()}
            </div>
            <p className="text-xs text-indigo-200/80 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              Sufficient for ~{estimatedLessons} interactive lessons
            </p>
          </div>
          <div className="pt-4 mt-4 border-t border-indigo-800/60">
            <button
              onClick={() => setIsBuyModalOpen(true)}
              className="w-full py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer"
            >
              Top Up Credits <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Total Consumed */}
        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Credits Consumed
              </span>
              <span className="w-7 h-7 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <TrendingUp className="w-4 h-4" />
              </span>
            </div>
            <div className="text-3xl font-extrabold text-slate-900 tracking-tight">
              {totalConsumed.toLocaleString()}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              All-time completed generation jobs
            </p>
          </div>
          <div className="pt-4 mt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
            <span>Rate per lesson:</span>
            <span className="font-bold text-slate-700">{creditsPerLesson} Credit</span>
          </div>
        </div>

        {/* Total Purchased */}
        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Credits Purchased
              </span>
              <span className="w-7 h-7 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4" />
              </span>
            </div>
            <div className="text-3xl font-extrabold text-slate-900 tracking-tight">
              {totalPurchased.toLocaleString()}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Lifetime acquired packages & grants
            </p>
          </div>
          <div className="pt-4 mt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
            <span>Org Plan:</span>
            <span className="font-bold uppercase text-indigo-600">
              {stats?.plan_tier || "Standard"}
            </span>
          </div>
        </div>

        {/* Seat Quota */}
        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Seat Utilization
              </span>
              <span
                className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                  seatUtilization.isAtLimit
                    ? "bg-rose-100 text-rose-700"
                    : seatUtilization.isNearLimit
                    ? "bg-amber-100 text-amber-700"
                    : "bg-emerald-100 text-emerald-700"
                }`}
              >
                {seatUtilization.percentage}% Used
              </span>
            </div>
            <div className="text-3xl font-extrabold text-slate-900 tracking-tight">
              {seatUtilization.used} <span className="text-base text-slate-400 font-medium">/ {seatUtilization.total}</span>
            </div>
            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-3">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  seatUtilization.isAtLimit
                    ? "bg-rose-500"
                    : seatUtilization.isNearLimit
                    ? "bg-amber-500"
                    : "bg-indigo-600"
                }`}
                style={{ width: `${seatUtilization.percentage}%` }}
              />
            </div>
          </div>
          <div className="pt-4 mt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
            <span>Available Seats:</span>
            <span className="font-bold text-slate-700">
              {Math.max(0, seatUtilization.total - seatUtilization.used)}
            </span>
          </div>
        </div>
      </div>

      {/* Expanded Credit Pricing Section */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="p-2 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600">
                <Sparkles className="w-5 h-5" />
              </span>
              <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                AI Generation Credit Packages
              </h3>
            </div>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Replenish your balance to generate custom courses, grounded SOP curricula, and interactive micro-lessons for your organization.
            </p>
          </div>
          <div className="flex items-center gap-2.5 text-xs font-semibold text-slate-600 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-200/80">
            <Coins className="w-4 h-4 text-amber-500" />
            <span>Active Balance: <strong className="text-slate-900 font-mono text-sm">{availableCredits}</strong> Credits</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {CREDIT_PACKAGES.map((pkg) => {
            const isGrowth = pkg.id === "growth";
            const isEnterprise = pkg.id === "enterprise";
            return (
              <div
                key={pkg.id}
                className={`relative rounded-3xl p-7 sm:p-8 flex flex-col justify-between transition-all duration-200 border-2 ${
                  isGrowth
                    ? "border-indigo-600 bg-gradient-to-b from-indigo-50/50 via-white to-white shadow-lg ring-1 ring-indigo-500/20"
                    : isEnterprise
                    ? "border-slate-800 bg-gradient-to-b from-slate-900/5 via-white to-white shadow-md hover:border-slate-700"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md"
                }`}
              >
                {pkg.badge && (
                  <div className="absolute -top-3.5 right-6">
                    <span
                      className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shadow-xs ${
                        isGrowth
                          ? "bg-indigo-600 text-white ring-2 ring-white"
                          : "bg-slate-900 text-white ring-2 ring-white"
                      }`}
                    >
                      {pkg.badge}
                    </span>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-bold text-slate-900">{pkg.name}</h4>
                    <span className="text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-lg">
                      {pkg.credits} Credits
                    </span>
                  </div>

                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
                      ${pkg.priceUsd}
                    </span>
                    <span className="text-xs font-bold text-slate-400 uppercase">USD</span>
                    <span className="text-xs font-medium text-slate-500">
                      (~{pkg.priceSar} SAR)
                    </span>
                  </div>

                  <p className="text-xs text-slate-500 min-h-[38px] leading-relaxed">
                    {pkg.description}
                  </p>

                  <div className="pt-5 mt-5 border-t border-slate-100">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">
                      Package Features & Capabilities
                    </p>
                    <ul className="space-y-3 text-xs text-slate-600">
                      {pkg.features.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span className="leading-snug">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-slate-100">
                  <button
                    onClick={() => handleSelectAndCheckout(pkg)}
                    className={`w-full py-3.5 px-4 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer shadow-xs ${
                      isGrowth
                        ? "bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow-indigo-200"
                        : isEnterprise
                        ? "bg-slate-900 hover:bg-slate-800 text-white"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-900"
                    }`}
                  >
                    <span>Proceed to Checkout</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Credit Usage Ledger Table */}
      <div className="bg-white border border-slate-200 rounded-3xl shadow-xs overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <Clock className="w-4 h-4 text-indigo-600" />
              AI Credit Transaction Ledger
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Transparent chronological audit trail of all credit purchases, reservations, and settlements.
            </p>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1 text-xs">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setOffset(0);
                }}
                className="bg-transparent text-slate-700 text-xs font-medium focus:outline-none cursor-pointer"
                aria-label="Filter transactions by type"
              >
                <option value="">All Types</option>
                <option value="purchase">Purchase</option>
                <option value="hold">Hold (Reserved)</option>
                <option value="settle">Settle</option>
                <option value="refund">Refund</option>
                <option value="admin_grant">Admin Grant</option>
              </select>
            </div>

            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1 text-xs">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setOffset(0);
                }}
                className="bg-transparent text-slate-700 text-xs font-medium focus:outline-none cursor-pointer"
                aria-label="Filter transactions by status"
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="settled">Settled</option>
                <option value="held">Held</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          {loadingTx ? (
            <div className="py-16 text-center text-slate-500 flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-3" />
              <p className="text-xs font-medium">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <Coins className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-semibold text-slate-700">No transactions found</p>
              <p className="text-xs text-slate-400 mt-1">
                Purchases, reservations, and refunds will appear here.
              </p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100 text-slate-500 uppercase tracking-wider font-semibold">
                  <th className="py-3.5 px-6">Date & Time</th>
                  <th className="py-3.5 px-4">Type</th>
                  <th className="py-3.5 px-4">Amount</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Balance After</th>
                  <th className="py-3.5 px-6">Notes / Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-slate-50/50 transition">
                    <td className="py-3.5 px-6 font-mono text-slate-500 whitespace-nowrap">
                      {new Date(tx.created_at).toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      {getTxTypeBadge(tx.transaction_type)}
                    </td>
                    <td className="py-3.5 px-4 font-bold whitespace-nowrap">
                      <span
                        className={
                          tx.amount > 0
                            ? "text-emerald-600"
                            : tx.amount < 0
                            ? "text-rose-600"
                            : "text-slate-600"
                        }
                      >
                        {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                      </span>{" "}
                      Credits
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      {getStatusPill(tx.status)}
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-slate-900 whitespace-nowrap">
                      {tx.balance_after}
                    </td>
                    <td className="py-3.5 px-6 text-slate-500 max-w-xs truncate">
                      {tx.notes || (tx.reference_id ? `Ref: ${tx.reference_id}` : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        {totalTx > limit && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <div>
              Showing <span className="font-semibold text-slate-800">{offset + 1}</span> to{" "}
              <span className="font-semibold text-slate-800">
                {Math.min(offset + limit, totalTx)}
              </span>{" "}
              of <span className="font-semibold text-slate-800">{totalTx}</span> transactions
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset((prev) => Math.max(0, prev - limit))}
                disabled={offset === 0}
                className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition cursor-pointer flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Previous
              </button>
              <button
                onClick={() => setOffset((prev) => prev + limit)}
                disabled={offset + limit >= totalTx}
                className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition cursor-pointer flex items-center gap-1"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Buy AI Credits Modal */}
      {isBuyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-4xl w-full p-6 sm:p-8 shadow-2xl relative overflow-hidden border border-slate-100 max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsBuyModalOpen(false)}
              className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 transition cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center shadow-xs">
                <Coins className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                  Purchase AI Course Credits
                </h3>
                <p className="text-xs text-slate-500">
                  Select an AI generation package to replenish your organization balance instantly.
                </p>
              </div>
            </div>

            {/* Package Options */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 my-6">
              {CREDIT_PACKAGES.map((pkg) => {
                const isSelected = selectedPkg.id === pkg.id;
                return (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedPkg(pkg)}
                    className={`p-6 rounded-2xl border-2 transition cursor-pointer flex flex-col justify-between relative ${
                      isSelected
                        ? "border-indigo-600 bg-indigo-50/50 shadow-md ring-2 ring-indigo-600/20"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    {pkg.badge && (
                      <span className="absolute -top-3 right-4 px-2.5 py-0.5 bg-indigo-600 text-white rounded-full text-[10px] font-extrabold uppercase tracking-wider shadow-xs">
                        {pkg.badge}
                      </span>
                    )}
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 mb-1">{pkg.name}</h4>
                      <div className="flex items-baseline gap-1 mb-2">
                        <span className="text-3xl font-black text-slate-900">{pkg.credits}</span>
                        <span className="text-xs font-semibold text-slate-500">Credits</span>
                      </div>
                      <div className="text-xs font-bold text-indigo-600 mb-3">
                        ${pkg.priceUsd} USD <span className="text-slate-400 font-normal">({pkg.priceSar} SAR)</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed mb-4 min-h-[36px]">
                        {pkg.description}
                      </p>
                    </div>

                    <ul className="space-y-2 pt-4 border-t border-slate-100 text-xs text-slate-600">
                      {pkg.features.map((feat, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
                          <span className="leading-snug">{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            {/* Summary & Checkout Action */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <span className="text-xs text-slate-500">Selected Order:</span>
                <div className="text-sm font-bold text-slate-900">
                  {selectedPkg.name} ({selectedPkg.credits} AI Credits) — ${selectedPkg.priceUsd} USD
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  You will be redirected to the secure payment checkout to complete this transaction.
                </p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setIsBuyModalOpen(false)}
                  className="px-4 py-2.5 border border-slate-200 hover:bg-white text-slate-700 text-xs font-bold rounded-xl transition cursor-pointer w-1/2 sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePurchase}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white text-xs font-bold rounded-xl shadow-xs transition flex items-center justify-center gap-2 cursor-pointer w-1/2 sm:w-auto"
                >
                  <Zap className="w-3.5 h-3.5 text-amber-300 fill-amber-300" /> Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
